import {
  BurnModifierContent,
  ChainLightningModifierContent,
  SlowModifierContent,
  WeaponBehaviorType,
  WeaponContent,
  WeaponLevel
} from '../data';
import { Logger } from '../utils/Logger';

export interface WeaponModifierRuntimePayload {
  readonly bonusDamage?: number;
  readonly areaMultiplier?: number;
  readonly critChance?: number;
  readonly burn?: BurnModifierContent;
  readonly slow?: SlowModifierContent;
  readonly chainLightning?: ChainLightningModifierContent;
  readonly chainHit?: number;
}

export interface WeaponRuntimeStats {
  readonly speed: number;
  readonly damage: number;
  readonly life: number;
  readonly pierce: number;
  readonly spread: number;
  readonly area: number;
  readonly count: number;
  readonly range?: number;
  readonly duration?: number;
  readonly tickRate?: number;
  readonly returnSpeed?: number;
  readonly chainHit?: number;
  readonly modifiers: WeaponModifierRuntimePayload;
}

export interface WeaponDamagePayload {
  readonly baseDamage: number;
  readonly bonusDamage: number;
  readonly areaMultiplier: number;
  readonly critChance: number;
  readonly burn?: BurnModifierContent;
  readonly slow?: SlowModifierContent;
  readonly chainLightning?: ChainLightningModifierContent;
  readonly chainHit: number;
}

export interface WeaponRuntimeConfig {
  readonly id: string;
  readonly type: WeaponBehaviorType;
  readonly sprite: string;
  readonly fireRate: number;
  readonly projectile: WeaponRuntimeStats;
  readonly damage: WeaponDamagePayload;
}

export interface AimVector {
  readonly x: number;
  readonly y: number;
}

export interface AimVectorProvider {
  getAimVector(): AimVector | null;
}

const SUPPORTED_MODIFIER_KEYS = new Set([
  'bonusDamage',
  'areaMultiplier',
  'critChance',
  'burn',
  'slow',
  'chainLightning',
  'chainHit'
]);

export function createWeaponRuntimeConfig(
  weapon: WeaponContent,
  level: WeaponLevel,
  logger: Logger
): WeaponRuntimeConfig | null {
  const stats = normalizeWeaponRuntimeStats(weapon, level, logger);
  if (!stats) {
    return null;
  }

  const damagePayload = createDamagePayload(stats);
  const fireRate = level.fireRate || weapon.baseFireRate;

  const runtimeConfig: WeaponRuntimeConfig = {
    id: weapon.id,
    type: weapon.type,
    sprite: weapon.sprite,
    fireRate,
    projectile: stats,
    damage: damagePayload
  };

  logger.debug('weapon:runtime-config-created', {
    weaponId: weapon.id,
    behavior: weapon.type,
    fireRate,
    stats
  });

  return runtimeConfig;
}

function normalizeWeaponRuntimeStats(
  weapon: WeaponContent,
  level: WeaponLevel,
  logger: Logger
): WeaponRuntimeStats | null {
  const mergedStats = { ...weapon.baseStats, ...level.projectile };
  const modifiers = normalizeModifiers(mergedStats.modifiers, weapon.id, logger);
  const chainHit = toPositiveOptionalNumber(mergedStats.chainHit) ?? toPositiveOptionalNumber(modifiers.chainHit);

  const runtimeStats: WeaponRuntimeStats = {
    speed: toNonNegativeNumber(mergedStats.speed, 0),
    damage: toNonNegativeNumber(mergedStats.damage, 0),
    life: toNonNegativeNumber(mergedStats.life, 0),
    pierce: toNonNegativeNumber(mergedStats.pierce, 0),
    spread: toNonNegativeNumber(mergedStats.spread, 0),
    area: toNonNegativeNumber(mergedStats.area, 0),
    count: toNonNegativeNumber(mergedStats.count, 1),
    range: toPositiveOptionalNumber(mergedStats.range),
    duration: toPositiveOptionalNumber(mergedStats.duration),
    tickRate: toPositiveOptionalNumber(mergedStats.tickRate),
    returnSpeed: toPositiveOptionalNumber(mergedStats.returnSpeed),
    chainHit,
    modifiers
  };

  if (!hasRequiredBehaviorData(weapon.id, weapon.type, runtimeStats, logger)) {
    return null;
  }

  logger.debug('weapon:runtime-stats-normalized', {
    weaponId: weapon.id,
    behavior: weapon.type,
    level: level.level,
    stats: runtimeStats
  });

  return runtimeStats;
}

function normalizeModifiers(raw: unknown, weaponId: string, logger: Logger): WeaponModifierRuntimePayload {
  const modifiers = asRecord(raw);
  if (!modifiers) {
    return {};
  }

  for (const key of Object.keys(modifiers)) {
    if (!SUPPORTED_MODIFIER_KEYS.has(key)) {
      logger.warn('weapon:unsupported-modifier-ignored', { weaponId, modifierKey: key });
    }
  }

  const burn = asRecord(modifiers.burn);
  const slow = asRecord(modifiers.slow);
  const chainLightning = asRecord(modifiers.chainLightning);

  return {
    bonusDamage: toPositiveOptionalNumber(modifiers.bonusDamage),
    areaMultiplier: toPositiveOptionalNumber(modifiers.areaMultiplier),
    critChance: toZeroToOneOptionalNumber(modifiers.critChance),
    burn: burn ? {
      damagePerSecond: toNonNegativeNumber(burn.damagePerSecond, 0),
      duration: toNonNegativeNumber(burn.duration, 0)
    } : undefined,
    slow: slow ? {
      multiplier: toZeroToOneOptionalNumber(slow.multiplier) ?? 0,
      duration: toNonNegativeNumber(slow.duration, 0)
    } : undefined,
    chainLightning: chainLightning ? {
      maxTargets: toNonNegativeNumber(chainLightning.maxTargets, 0),
      radius: toNonNegativeNumber(chainLightning.radius, 0),
      falloff: toZeroToOneOptionalNumber(chainLightning.falloff)
    } : undefined,
    chainHit: toPositiveOptionalNumber(modifiers.chainHit)
  };
}

function hasRequiredBehaviorData(
  weaponId: string,
  behavior: WeaponBehaviorType,
  stats: WeaponRuntimeStats,
  logger: Logger
): boolean {
  if (behavior === 'area' && stats.area <= 0) {
    logger.error('weapon:missing-required-behavior-data', { weaponId, behavior, field: 'area' });
    return false;
  }

  if (behavior === 'beam' && (!stats.range || stats.range <= 0)) {
    logger.error('weapon:missing-required-behavior-data', { weaponId, behavior, field: 'range' });
    return false;
  }

  if (behavior === 'boomerang') {
    if (!stats.returnSpeed || stats.returnSpeed <= 0) {
      logger.error('weapon:missing-required-behavior-data', { weaponId, behavior, field: 'returnSpeed' });
      return false;
    }
    if (stats.life <= 0) {
      logger.error('weapon:missing-required-behavior-data', { weaponId, behavior, field: 'life' });
      return false;
    }
  }

  if (behavior === 'chain') {
    const hasChainHit = Boolean(stats.chainHit && stats.chainHit > 0);
    const hasChainLightning = Boolean(stats.modifiers.chainLightning && stats.modifiers.chainLightning.maxTargets > 0);
    if (!hasChainHit && !hasChainLightning) {
      logger.error('weapon:missing-required-behavior-data', { weaponId, behavior, field: 'chainHit|chainLightning.maxTargets' });
      return false;
    }
  }

  return true;
}

function createDamagePayload(stats: WeaponRuntimeStats): WeaponDamagePayload {
  return {
    baseDamage: stats.damage,
    bonusDamage: stats.modifiers.bonusDamage ?? 0,
    areaMultiplier: stats.modifiers.areaMultiplier ?? 1,
    critChance: stats.modifiers.critChance ?? 0,
    burn: stats.modifiers.burn,
    slow: stats.modifiers.slow,
    chainLightning: stats.modifiers.chainLightning,
    chainHit: stats.chainHit ?? stats.modifiers.chainHit ?? 0
  };
}

function toNonNegativeNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}

function toPositiveOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function toZeroToOneOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1 ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? value as Record<string, unknown> : null;
}
